import React, { useRef, useMemo, useEffect } from "react";
import {
  useGetUserInfoQuery,
  useLogoutMutation,
  useGetUnreadInboxCountQuery,
} from "../../services/apiSlice";
import { useNavigate } from "react-router-dom";
import images from "../../images";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";

import "react-image-crop/dist/ReactCrop.css";
import NewHeader from "../Home/NewHeader";
import ProfileCard from "./ProfileCommon/RallyCoinsCard";
import ProfileChevron from "./ProfileCommon/ProfileChevron";
import CommonModal from "../../components/common/Modal/ModalCommon";
import Button from "../../components/common/Button/Button";
import posthog from "posthog-js";
import { usePostHog } from "../../hooks/usePostHog";

function Profile() {
  const navigate = useNavigate();
  const [logOut, { isLoading: isLoadLogout }] = useLogoutMutation();
  const { data: userInfo, isLoading: isLoadingProfile } = useGetUserInfoQuery({
    refetchOnMountOrArgChange: true,
  });
  const { data: unreadData } = useGetUnreadInboxCountQuery();

  const { t } = useTranslation();
  const { trackEvent } = usePostHog();
  const logoutModalRef = useRef();

  const unreadCount = unreadData?.total || 0;

  // Track profile page viewed
  useEffect(() => {
    if (userInfo?.data) {
      trackEvent('profile_page_viewed', {
        user_level: userInfo.data.level || 0,
        user_xp: userInfo.data.xp || 0,
        user_coins: userInfo.data.token || 0,
        account_status: userInfo.data.status,
        unread_notifications: unreadCount,
        has_phone: !!userInfo.data.phone,
        has_avatar: !!userInfo.data.avatar_data?.file_path
      });
    }
  }, [userInfo, unreadCount, trackEvent]);

  const genderMapping = useMemo(() => {
    return {
      male: t("signUp.gender.male"),
      female: t("signUp.gender.female"),
      maleIdentifying: t("signUp.gender.maleIdentifying"),
      femaleIdentifying: t("signUp.gender.femaleIdentifying"),
      nonBinary: t("signUp.gender.nonBinary"),
      agender: t("signUp.gender.agender"),
      preferNotToSay: t("signUp.gender.preferNotToSay"),
    };
  }, [t]);

  const handleLogOut = async () => {
    try {
      const allKeys = Object.keys(localStorage);
      const timerKeys = allKeys.filter(
        (key) => key.startsWith("timer_") || key.startsWith("initialTime_")
      );

      timerKeys.forEach((key) => localStorage.removeItem(key));

      const response = await logOut();
      if (response.data.message === "Operation successful") {
        localStorage.clear();
        sessionStorage.clear();
        posthog.reset();
        window.location.href = "/sign-in";
      }
    } catch (err) {
      console.error("Error:", err);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/sign-in";
    }
  };
  const handleEditUser = () => {
    navigate(`/profile/edit`);
  };

  const handleWithdraw = () => {
    navigate(`/profile/withdraw`);
  };

  const handleInbox = () => {
    navigate(`/profile/inbox`);
  };
  const handleQuestHistory = () => {
    navigate(`/profile/quest/history`);
  };

  const handleSupport = () => {
    navigate(`/profile/support`);
  };

  // const handlePowerUp = () => {
  //   navigate(`/profile/power-up`);
  // };

  return (
    <div className="flex flex-col mx-auto  bg-white w-full max-w-[480px]">
      <Helmet>
        <meta name="theme-color" content="#FFFFFF" />
      </Helmet>
      <LoadingCommon statusLoading={isLoadingProfile || isLoadLogout} />
      <div className="fixed top-0 left-0 w-full bg-white z-50">
        <NewHeader />
      </div>
      <div className="h-[60px]"></div>
      <div className="flex flex-col w-full min-h-[912px] px-[31px]">
        <div className="flex flex-col flex-1 py-4 w-full gap-[18px]">
          <div>
            <div className="flex flex-row items-center gap-2.5">
              <h1 className="text-4xl font-bold text-[#253FF6]">
                {userInfo?.data?.username
                  ? userInfo?.data?.username
                  : t("profile.username")}
              </h1>
              <img
                onClick={handleEditUser}
                className="w-6 cursor-pointer"
                src={images.EditIconBlue}
                alt=""
              />
            </div>
            <div>
              <span className="text-xl font-medium text-[#253FF6]">
                {userInfo?.data?.age},{" "}
                {genderMapping[userInfo?.data?.gender] ||
                  userInfo?.data?.gender}
                , Singapore (SGD)
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-[18px] mt-4">
            <ProfileCard
              coinAmount={userInfo?.data?.token ?? "0"}
              dollarValue={
                userInfo?.data?.token ? userInfo?.data?.token / 100 : "0"
              }
              onWithdraw={handleWithdraw}
              subTitle={t("profile.rallyCoins")}
              imageCard={images.RallyCoinSmallBlueIcon}
            />
            <ProfileCard
              className="mt-4 bg-[#f4f2f2] "
              subTitle={t("profile.rallyXP")}
              currentXp={userInfo?.data?.xp ?? "0"}
              total={userInfo?.data?.level_object.max_xp || 0}
              imageCard={images.RallyXPSmallBlueIcon}
              level={userInfo?.data?.level_object.no}
            />
            {/* 
            <ProfileCard
              className="mt-4 bg-[#F8F4FC] "
              subTitle={t("profile.powerUp")}
              imageCard={images.RallyPowerUpImage}
              subPowerUpImage={images.SubPowerUpImage}
              subPowerUpImageSecond={images.SubPowerUpImageSecond}
              onClickCard={handlePowerUp}
            /> */}
          </div>
          <div>
            <ProfileChevron
              title={t("profile.inbox")}
              isShowNotificationBadge={true}
              onClick={handleInbox}
              unreadCount={unreadCount}
            />
            <ProfileChevron
              title={t("profile.questHistory")}
              onClick={handleQuestHistory}
            />
            <ProfileChevron
              title={t("profile.support")}
              onClick={handleSupport}
            />
          </div>

          <div className="w-full flex justify-center">
            <button
              onClick={() => logoutModalRef.current.openModal()}
              className="gap-2.5 self-stretch px-5 max-w-[368px] mt-5 w-full text-sm font-medium tracking-tight text-center text-[#253FF6] bg-white rounded-md border border-[#253FF6] border-solid min-h-[50px]"
            >
              {t("profile.logout")}
            </button>
          </div>
          {/* <div className="text-center mt-6 mb-4">
            <a
              href="mailto:hello@letsrally.ai"
              className="text-sm text-gray-500 hover:text-indigo-600 hover:underline flex items-center justify-center gap-1.5"
            >
              <img
                loading="lazy"
                src={
                  images.EmailIcon ||
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'%3E%3C/path%3E%3C/svg%3E"
                }
                className="w-4 h-4"
                alt="Email icon"
              />
              {t("profile.contactSupport")}
            </a>
          </div> */}
        </div>
      </div>
      <CommonModal
        ref={logoutModalRef}
        title={t("profile.logoutConfirmation")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-4 py-2 rounded-xl bg-white border border-stone-200 text-[#A0A0A0]"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.yes")}
              className="flex-1 px-4 py-2 rounded-xl text-white bg-[#253FF6] disabled:opacity-50"
              variant="primary"
              onClick={handleLogOut}
              // disabled={isUpdating}
            />
          </>
        )}
      ></CommonModal>
    </div>
  );
}

export default Profile;
